// Copyright (c) Microsoft. All rights reserved.

import { makeStyles, shorthands, tokens } from '@fluentui/react-components';
import React from 'react';
import { GetResponseOptions, useChat } from '../../libs/hooks/useChat';
import { useAppDispatch, useAppSelector } from '../../redux/app/hooks';
import { RootState } from '../../redux/app/store';
import { FeatureKeys, Features } from '../../redux/features/app/AppState';
import { SharedStyles } from '../../styles';
import { ChatInput } from './ChatInput';
import { ChatHistory } from './chat-history/ChatHistory';
import { ChatPresets } from './ChatPresets';
import { ChatDisclaimer } from './ChatDisclaimer';
import { editConversationTitle, setConversationLoadState } from '../../redux/features/conversations/conversationsSlice';
import { ConversationLoadState } from '../../redux/features/conversations/ConversationsState';
import { Loading } from '../views';
import { ChatContinueBanner } from './chat-history/ChatContinueBanner';

const useClasses = makeStyles({
    root: {
        ...shorthands.overflow('hidden'),
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        height: '100%',
    },
    scroll: {
        display: 'flex',
        flexDirection: 'column',
        ...shorthands.margin(tokens.spacingVerticalXS),
        ...SharedStyles.scroll,
    },
    presets: {
        ...shorthands.padding(tokens.spacingVerticalM),
        paddingLeft: tokens.spacingHorizontalM,
        paddingRight: tokens.spacingHorizontalM,
        display: 'flex',
        justifyContent: 'center',
    },
    history: {
        ...shorthands.padding(tokens.spacingVerticalM),
        paddingLeft: tokens.spacingHorizontalM,
        paddingRight: tokens.spacingHorizontalM,
        display: 'flex',
        justifyContent: 'center',
    },
    spacer: {
        flexGrow: 1,
    },
    input: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'center',
        ...shorthands.padding(tokens.spacingVerticalS, tokens.spacingVerticalNone),
    },
});

export const ChatRoom: React.FC = () => {
    const classes = useClasses();
    const chat = useChat();
    const dispatch = useAppDispatch();

    const { conversations, selectedId } = useAppSelector((state: RootState) => state.conversations);
    const messages = conversations[selectedId].messages;

    const scrollViewTargetRef = React.useRef<HTMLDivElement>(null);
    const [shouldAutoScroll, setShouldAutoScroll] = React.useState(true);

    React.useEffect(() => {
        if (!shouldAutoScroll) return;
        scrollViewTargetRef.current?.scrollTo(0, scrollViewTargetRef.current.scrollHeight);
    }, [messages, shouldAutoScroll]);

    React.useEffect(() => {
        const onScroll = () => {
            if (!scrollViewTargetRef.current) return;
            const { scrollTop, scrollHeight, clientHeight } = scrollViewTargetRef.current;
            const isAtBottom = scrollTop + clientHeight >= scrollHeight - 10;
            setShouldAutoScroll(isAtBottom);
        };

        if (!scrollViewTargetRef.current) return;

        const currentScrollViewTarget = scrollViewTargetRef.current;

        currentScrollViewTarget.addEventListener('scroll', onScroll);
        return () => {
            currentScrollViewTarget.removeEventListener('scroll', onScroll);
        };
    }, []);

    const handleSubmit = async (options: GetResponseOptions) => {
        await Promise.all([chat.getResponse(options), updateTitleOnInitialQuestion(options.value)]);
        setShouldAutoScroll(true);
    };

    const updateTitleOnInitialQuestion = async (newTitle: string) => {
        const chatState = conversations[selectedId];
        if (chatState.messages.length > 1) {
            return;
        }
        return chat.editChat(selectedId, newTitle, chatState.systemDescription, chatState.memoryBalance).then(() => {
            dispatch(editConversationTitle({ id: selectedId, newTitle }));
        });
    };

    React.useEffect(() => {
        if (conversations[selectedId].loadState > ConversationLoadState.Unloaded) {
            console.log('skip load');
            return;
        }

        dispatch(setConversationLoadState({ id: selectedId, newLoadState: ConversationLoadState.Loading }));
        void chat.loadMessages(selectedId);
    }, [chat, conversations, dispatch, selectedId]);

    /* function handleCallback (childData: GetResponseOptions) {
      handleSubmit(childData);
    }; */

    if (conversations[selectedId].hidden) {
        return (
            <div className={classes.root}>
                <div className={classes.scroll}>
                    <div className={classes.history}>
                        <h3>
                            This conversation is not visible in the app because{' '}
                            {Features[FeatureKeys.MultiUserChat].label} is disabled. Please enable the feature in the
                            settings to view the conversation, select a different one, or create a new conversation.
                        </h3>
                    </div>
                </div>
            </div>
        );
    }

    if (conversations[selectedId].loadState < ConversationLoadState.Loaded) {
        return (
            <div className={classes.root}>
                <Loading text={'Loading messages...'} />
            </div>
        );
    }

    const showContinueBanner = messages.length > 1 && messages.at(-1)?.userId === 'Bot';

    return (
        <div className={classes.root}>
            <ChatDisclaimer />
            <div ref={scrollViewTargetRef} className={classes.scroll}>
                {messages.length < 2 && (
                    <div className={classes.presets}>
                        <ChatPresets addMessage={handleSubmit} />
                    </div>
                )}
                <div className={classes.spacer} />
                <div className={classes.history}>
                    <ChatHistory messages={messages} />
                </div>
            </div>

            {showContinueBanner && (
                <div className={classes.input}>
                    <ChatContinueBanner />
                </div>
            )}

            <div className={classes.input}>
                <ChatInput onSubmit={handleSubmit} />
            </div>
        </div>
    );
};
